/**
 * Deep link into the existing accommodation rates page so an anomaly can be confirmed by
 * hand, rather than reimplementing a live availability check here.
 *
 * That page is entirely query-driven (see AccommodationPropertyRatesSection), so we can
 * preset the property, a stay night inside the affected range, occupancy and the search
 * flags. Someone clicks through, hits Search, and either a rate comes back - in which case
 * the alert is wrong - or nothing does, which confirms it.
 *
 * Defaults mirror the URL commercial already use by hand, so the link reproduces the check
 * they would have run themselves: a customer-facing, hotel-only search rather than a
 * debug-everything one. allRates and doDebug are exposed for when the answer is "nothing
 * came back and I want to know why".
 */

export interface VerifyLinkParams {
  /** svc-accommodation property id - NOT the svc-reservation id the ARI logs carry */
  accommodationPropertyId: string;
  /** stay night to check - must sit inside the anomaly's affected range */
  checkIn: string;
  /** defaults to checkIn + 1 night */
  checkOut?: string;
  adults?: number;
  childrenAges?: Array<number>;
  region?: string;
  currency?: string;
  brand?: string;
  /** include rates that did not survive pricing - useful once a search comes back empty */
  allRates?: boolean;
  /** expose the pricing debug panel */
  doDebug?: boolean;
}

/**
 * Stay dates arrive as a bare date from some rules and a full timestamp from others, and are
 * absent entirely on connection-grain cases, so every date here is parsed rather than
 * concatenated.
 */
function parseStayDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function stayDate(value: string | null | undefined): string | null {
  return parseStayDate(value)?.toISOString().slice(0, 10) ?? null;
}

const addOneNight = (date: string): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** Occupancy is `adults-childAge1,childAge2`, per parseOccupancyString on the rates page. */
export const occupancyString = (adults: number, childrenAges: Array<number> = []): string =>
  childrenAges.length > 0 ? `${adults}-${childrenAges.join(',')}` : `${adults}`;

export function buildRatesVerifyUrl({
  accommodationPropertyId,
  checkIn,
  checkOut,
  adults = 2,
  childrenAges = [],
  region = 'AU',
  currency = 'AUD',
  brand = 'luxuryescapes',
  allRates = false,
  doDebug = false,
}: VerifyLinkParams): string {
  const params = new URLSearchParams({
    checkIn,
    checkOut: checkOut ?? addOneNight(checkIn),
    occupancies: occupancyString(adults, childrenAges),
    region,
    brand,
    currency,
    doDebug: String(doDebug),
    ratesFilter: 'hotel-only',
    allRates: String(allRates),
    dynamicAvailability: 'false',
    allowMysteryRooms: 'true',
    useV2: 'true',
  });

  return `/accommodation/properties/${accommodationPropertyId}/rates?${params.toString()}`;
}

/** Midpoint of the affected range - more representative than the first night. */
export function suggestedCheckIn(
  firstStayDate: string | null | undefined,
  lastStayDate: string | null | undefined,
): string | null {
  const first = parseStayDate(firstStayDate);
  const last = parseStayDate(lastStayDate) ?? first;
  if (!first || !last) {
    return null;
  }
  return new Date(first.getTime() + (last.getTime() - first.getTime()) / 2).toISOString().slice(0, 10);
}

/**
 * Deep link to the vendor room-rate dates calendar.
 *
 * Better suited to ARI anomalies than the accommodation link: it keys off svc-reservation
 * ids, which is exactly what the ARI logs already carry (property_id, room_type_ids,
 * room_rate_ids). The accommodation rates page needs a separate id resolution because
 * svc-accommodation properties live in their own id space, mapped back via
 * mappings_cache.reservation_property_ids.
 *
 * The two answer different questions, so both are worth having: this one shows what we hold
 * for that room rate day by day, the rates page shows what a customer would actually be
 * quoted.
 */
export interface VendorRoomRateLinkParams {
  vendorId: string;
  propertyId: string;
  roomTypeId: string;
  roomRateId: string;
}

export function buildVendorRoomRateDatesUrl({
  vendorId,
  propertyId,
  roomTypeId,
  roomRateId,
}: VendorRoomRateLinkParams): string {
  return `/vendors/${vendorId}/properties/${propertyId}/room-types/${roomTypeId}/room-rates/${roomRateId}/dates`;
}
